import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry-supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { DataTable, type Column } from '../components/ui/DataTable';
import {
  RevealablePhiCell,
  type RevealField,
  type RevealFn,
} from '../components/RevealablePhiCell';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';

// Patients list. Loads with plaintext addresses (RLS-scoped browser
// client) plus decrypted full_name via /api/patients/reveal-batch,
// both resolved BEFORE the table renders so the row and its name
// appear together.
//
// Name column: fetchPage runs the address query and the reveal-batch
// call, then setRows only fires once names have been merged into
// nameMap. The table shows "Loading patients…" while both complete
// rather than "row now / name later" (the split render was jarring
// enough that Dom flagged it 2026-08-19). Server RLS-scopes the
// ciphertext fetch, decrypts, and returns a { id -> plaintext } map.
// Names stay libsodium-encrypted at rest; the key never crosses the
// wire; the change from the earlier click-to-reveal UX is display
// only, not posture.
//
// Phone column: still click-to-reveal via /api/patients/reveal — one
// audit event per click. The batch endpoint deliberately does NOT
// audit-log per row (list-view autoreveal would generate 25+ events
// per page navigation and drown the audit table); intentional phone
// reveals + one-off detail views continue to leave a per-field trail.
//
// Search covers three surfaces:
//   1. Plaintext columns via a single PostgREST .or() .ilike chain:
//      external_patient_id, address_line_1, city, zip. Zip is
//      prefix-matched (%zip%) — everything else is substring.
//   2. Encrypted full_name via /api/patients/search — that route fetches
//      the caller's visible ciphertext, decrypts server-side, and
//      returns matching IDs. We hydrate those IDs with a second
//      PostgREST .in('id', ids) query so the row shape matches the
//      plaintext branch.
//   3. Results from (1) and (2) are unioned by id, sorted by created_at
//      desc. Infinite scroll is suspended while a search is active — we
//      show a single capped result set instead of paginating a merged
//      union, which would require server-side cursor logic across both
//      branches.
// Phone is left out of search on purpose: same encryption gate as name,
// and pharmacies never look patients up by phone.

const PAGE_SIZE = 25;

// When a search is active we skip infinite scroll and show a single
// capped result set (see file header for the union-branch rationale).
// Kept generous enough that a common search never truncates in
// practice; if it does we surface a note in the empty/loading strip.
const SEARCH_RESULT_CAP = 200;

// No full_name / phone here — those are revealed per-row via the API.
const PATIENT_LIST_COLUMNS =
  'id, external_patient_id, address_line_1, address_line_2, city, state, zip, date_of_birth, sms_consent, created_at';

export type PatientListRow = {
  id: string;
  external_patient_id: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  date_of_birth: string | null;
  sms_consent: boolean;
  created_at: string;
};

// Optional `q` keeps the URL clean (no `?q=` artifact) when empty,
// while still making the search term shareable + back-nav-preservable
// from the detail page.
export const Route = createFileRoute('/_authenticated/patients/')({
  component: PatientsListPage,
  validateSearch: (search: Record<string, unknown>): { q?: string } => {
    const raw = search['q'];
    if (typeof raw === 'string' && raw.trim().length > 0) return { q: raw };
    return {};
  },
});

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function addressDisplay(row: PatientListRow): string {
  const line1 = [row.address_line_1, row.address_line_2]
    .filter((p) => p && p.trim().length > 0)
    .join(' ');
  const cityState = [row.city, row.state].filter(Boolean).join(', ');
  const parts = [line1, cityState, row.zip ?? ''].filter(
    (p) => p && p.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(' · ') : '—';
}

// Allow letters, digits, space, hyphen, period, slash, hash — enough
// for typical US addresses ("123 Main St #4", "44312-1234") without
// letting PostgREST `.or()` parser metacharacters (`,` `(` `)` `'`)
// or ilike wildcards (`%` `_`) through into the URL filter. The
// browser client URL-encodes whatever survives this, so the remaining
// surface is harmless.
const SEARCH_TERM_MAX = 64;
const SEARCH_TERM_SANITIZER = /[^a-zA-Z0-9 \-./#]/g;

export function sanitizeSearchTerm(raw: string): string {
  return raw.trim().slice(0, SEARCH_TERM_MAX).replace(SEARCH_TERM_SANITIZER, '');
}

function PatientsListPage(): JSX.Element {
  const { q } = Route.useSearch();
  const navigate = useNavigate();
  const setQuery = useCallback(
    (next: string) => {
      void navigate({ to: '/patients', search: next ? { q: next } : {} });
    },
    [navigate],
  );
  const goToPatient = useCallback(
    (patientId: string) => {
      void navigate({
        to: '/patients/$patientId',
        params: { patientId },
        search: q ? { q } : {},
      });
    },
    [navigate, q],
  );
  return (
    <PatientsListView
      q={q ?? ''}
      onQueryChange={setQuery}
      onRowOpen={goToPatient}
    />
  );
}

type ViewProps = {
  q: string;
  onQueryChange: (next: string) => void;
  onRowOpen: (patientId: string) => void;
  // Test seam — defaults to the singleton supabase client.
  client?: LynrxSupabaseClient;
  // Test seam — defaults to the real /api/patients/reveal fetch.
  reveal?: RevealFn;
};

export function PatientsListView({
  q,
  onQueryChange,
  onRowOpen,
  client = defaultClient,
  reveal,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  useSetPageHeader({ title: 'Patients' });

  const [rows, setRows] = useState<PatientListRow[] | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When a search is active the result set is a client-side union of
  // the plaintext branch + the name-search API branch. We show a
  // "Searching…" strip instead of "Loading more…" and suppress the
  // infinite-scroll observer entirely — see file header.
  const [searchTruncated, setSearchTruncated] = useState(false);
  // Decrypted full_name keyed by patient id. Populated by
  // hydrateNamesFor (called inside fetchPage BEFORE setRows). Missing
  // id = "not yet hydrated" (renders as a placeholder — should be
  // unreachable in practice now); explicit null = decrypted to null
  // or RLS miss. A ref mirrors the current map so hydrateNamesFor
  // can skip already-resolved IDs without listing `nameMap` in its
  // dep list — otherwise fetchPage's identity would churn on every
  // hydrate and re-trigger the reset-on-session effect (infinite
  // loop).
  const [nameMap, setNameMap] = useState<Record<string, string | null>>({});
  const nameMapRef = useRef<Record<string, string | null>>({});
  useEffect(() => {
    nameMapRef.current = nameMap;
  }, [nameMap]);

  // Local input state syncs from the URL `q`. Pressing Enter / clicking
  // Search commits the input to the URL, which is the source of truth.
  const [inputValue, setInputValue] = useState(q);
  useEffect(() => setInputValue(q), [q]);

  // Awaited helper: POST the given ids to /api/patients/reveal-batch
  // and MERGE the returned names into nameMap. Returns after the map
  // has been updated (or the network attempt has failed silently) so
  // fetchPage can setRows immediately afterward, and the row + its
  // name render in the same paint. Only requests IDs we haven't
  // resolved yet — reuses prior page names on infinite-scroll append.
  const hydrateNamesFor = useCallback(
    async (ids: string[]): Promise<void> => {
      if (!session || ids.length === 0) return;
      // Read the live map via ref so this callback doesn't take
      // nameMap as a dep — see the ref definition above.
      const current = nameMapRef.current;
      const missing = ids.filter((id) => !(id in current));
      if (missing.length === 0) return;
      try {
        const res = await fetch('/api/patients/reveal-batch', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ patient_ids: missing }),
        });
        // Any failure — non-200, HTML fallback in a dev env, network
        // error — leaves the ids unresolved. Seed them to null so
        // the cell renders "—" instead of a spinner-forever
        // placeholder, and future page loads don't retry them until
        // the row goes out of scope.
        const seedNulls = () => {
          setNameMap((prev) => {
            const next: Record<string, string | null> = { ...prev };
            for (const id of missing) if (!(id in next)) next[id] = null;
            return next;
          });
        };
        if (!res.ok) {
          seedNulls();
          return;
        }
        const data = (await res.json().catch(() => null)) as
          | { names: Record<string, string | null> }
          | null;
        if (!data?.names) {
          seedNulls();
          return;
        }
        setNameMap((prev) => {
          const next: Record<string, string | null> = { ...prev };
          for (const id of missing) if (!(id in next)) next[id] = null;
          return { ...next, ...data.names };
        });
      } catch {
        setNameMap((prev) => {
          const next: Record<string, string | null> = { ...prev };
          for (const id of missing) if (!(id in next)) next[id] = null;
          return next;
        });
      }
    },
    [session],
  );

  const fetchPage = useCallback(
    async (nextPage: number, term: string) => {
      if (!session) return;
      setLoading(true);
      setError(null);
      const cleaned = sanitizeSearchTerm(term);

      // ─── Search mode ───────────────────────────────────────────────
      // Union of two branches: PostgREST .or() across plaintext
      // columns + POST /api/patients/search for encrypted full_name
      // matches. Both are RLS-scoped (the API route uses a
      // token-bound client). No pagination — capped at
      // SEARCH_RESULT_CAP.
      if (cleaned.length > 0) {
        setSearchTruncated(false);
        // Branch 1: plaintext OR chain. Zip is prefix-matched, the
        // rest are substring.
        const plainQuery = client
          .from('patients')
          .select(PATIENT_LIST_COLUMNS)
          .is('deleted_at', null)
          .or(
            [
              `external_patient_id.ilike.%${cleaned}%`,
              `address_line_1.ilike.%${cleaned}%`,
              `city.ilike.%${cleaned}%`,
              `zip.ilike.${cleaned}%`,
            ].join(','),
          )
          .order('created_at', { ascending: false })
          .range(0, SEARCH_RESULT_CAP);

        // Branch 2: encrypted-name match via API. Failure here
        // shouldn't sink the whole search — we still show the
        // plaintext hits and surface a soft warning.
        const nameSearchRes = fetch('/api/patients/search', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ q: cleaned }),
        }).then(async (res) => {
          if (!res.ok) throw new Error(`search_failed_${res.status}`);
          return res.json() as Promise<{ ids: string[]; truncated: boolean }>;
        });

        const [plainResult, nameResult] = await Promise.allSettled([
          plainQuery,
          nameSearchRes,
        ]);

        // Collect plaintext hits first — always available on success.
        let plainRows: PatientListRow[] = [];
        if (plainResult.status === 'fulfilled') {
          if (plainResult.value.error) {
            captureSupabaseError(
              plainResult.value.error,
              'patients.search.plaintext',
            );
          } else {
            plainRows = (plainResult.value.data ??
              []) as unknown as PatientListRow[];
          }
        }

        // Hydrate name-match IDs into full list rows via a normal
        // PostgREST .in — same RLS gate as the API route, so any ID
        // that survived server-side is safe to re-query here.
        let nameRows: PatientListRow[] = [];
        let nameTruncated = false;
        if (nameResult.status === 'fulfilled') {
          nameTruncated = nameResult.value.truncated;
          const ids = nameResult.value.ids;
          if (ids.length > 0) {
            const { data: hyd, error: hydErr } = await client
              .from('patients')
              .select(PATIENT_LIST_COLUMNS)
              .in('id', ids)
              .is('deleted_at', null);
            if (hydErr) {
              captureSupabaseError(hydErr, 'patients.search.hydrate');
            } else {
              nameRows = (hyd ?? []) as unknown as PatientListRow[];
            }
          }
        }

        // Union by id, preserve name-match rows first (usually the
        // higher-signal branch when a caller types a name), then
        // sort by created_at desc for a stable list.
        const seen = new Set<string>();
        const merged: PatientListRow[] = [];
        for (const r of [...nameRows, ...plainRows]) {
          if (seen.has(r.id)) continue;
          seen.add(r.id);
          merged.push(r);
        }
        merged.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
        const capped = merged.slice(0, SEARCH_RESULT_CAP);
        // Hydrate names BEFORE committing rows — the table shows
        // "Loading patients…" until both queries + the decrypt call
        // resolve, so the row and its name appear together.
        await hydrateNamesFor(capped.map((r) => r.id));
        setSearchTruncated(
          nameTruncated || merged.length > SEARCH_RESULT_CAP,
        );
        setRows(capped);
        setHasMore(false);
        setPage(0);
        setLoading(false);
        // Only surface an error banner when BOTH branches failed —
        // a partial result is more useful than a red banner.
        if (
          plainResult.status !== 'fulfilled' &&
          nameResult.status !== 'fulfilled'
        ) {
          setError('Could not run search.');
        }
        return;
      }

      // ─── Browse mode (no term) ────────────────────────────────────
      // Original paginated path — infinite scroll drives fetchPage
      // with an incrementing nextPage.
      const from = nextPage * PAGE_SIZE;
      const to = from + PAGE_SIZE; // one past end to peek for the next page
      const query = client
        .from('patients')
        .select(PATIENT_LIST_COLUMNS)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      const { data, error: err } = await query.range(from, to);
      if (err) {
        captureSupabaseError(err, 'patients.list.fetch');
        setError('Could not load patients.');
        setLoading(false);
        return;
      }
      const all = (data ?? []) as unknown as PatientListRow[];
      const pageRows = all.slice(0, PAGE_SIZE);
      // Same rationale as the search branch: hydrate names before
      // committing rows so the render is one-shot rather than
      // "row-then-name". Only newly-loaded IDs are decrypted (see
      // hydrateNamesFor's `missing` filter).
      await hydrateNamesFor(pageRows.map((r) => r.id));
      setRows((prev) =>
        nextPage === 0 ? pageRows : [...(prev ?? []), ...pageRows],
      );
      setHasMore(all.length > PAGE_SIZE);
      setPage(nextPage);
      setSearchTruncated(false);
      setLoading(false);
    },
    [client, session, hydrateNamesFor],
  );

  // Reset to page 0 on session ready OR whenever the URL `q` changes
  // (filter changed → old offset is meaningless against a new set).
  useEffect(() => {
    if (!isReady || !session) return;
    void fetchPage(0, q);
  }, [isReady, session, fetchPage, q]);

  // (Names are hydrated inline by fetchPage via hydrateNamesFor
  // BEFORE setRows fires — the old post-render useEffect was removed
  // 2026-08-19 because the row/name split render was visibly
  // jarring.)

  const onSubmitSearch = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      onQueryChange(inputValue);
    },
    [inputValue, onQueryChange],
  );
  const onClearSearch = useCallback(() => {
    setInputValue('');
    onQueryChange('');
  }, [onQueryChange]);

  // Default reveal: POST the caller's token to the server-side route,
  // which decrypts + logs access and returns the single plaintext value.
  const defaultReveal: RevealFn = useCallback(
    async (patientId: string, field: RevealField) => {
      if (!session) throw new Error('no_session');
      const res = await fetch('/api/patients/reveal', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ patient_id: patientId, field }),
      });
      if (!res.ok) {
        throw new Error(`reveal_failed_${res.status}`);
      }
      const data = (await res.json()) as { value: string | null };
      return data.value;
    },
    [session],
  );
  const revealFn = reveal ?? defaultReveal;

  const columns: Column<PatientListRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: (r) => {
          // Three states, keyed off the batch-reveal hydration:
          //   • id absent from nameMap → in flight, show a faint dash
          //   • id present but value null → RLS miss / null plaintext
          //   • id present with value → render plaintext
          if (!(r.id in nameMap)) {
            return <span className="text-ink-3">…</span>;
          }
          const name = nameMap[r.id];
          return <span className="text-ink">{name ?? '—'}</span>;
        },
      },
      {
        key: 'phone',
        header: 'Phone',
        render: (r) => (
          <RevealablePhiCell
            patientId={r.id}
            field="phone"
            label="phone"
            reveal={revealFn}
          />
        ),
      },
      { key: 'address', header: 'Address', render: addressDisplay },
      {
        key: 'date_of_birth',
        header: 'Date of birth',
        render: (r) => formatDate(r.date_of_birth),
      },
      {
        key: 'sms_consent',
        header: 'SMS',
        render: (r) => (r.sms_consent ? 'Consented' : '—'),
      },
      { key: 'created_at', header: 'Added', render: (r) => formatDate(r.created_at) },
    ],
    [revealFn, nameMap],
  );

  const hasActiveSearch = q.length > 0;
  const emptyMessage =
    rows === null
      ? 'Loading patients…'
      : hasActiveSearch
        ? 'No patients match this search.'
        : 'No patients yet. They are created automatically when you upload orders.';

  return (
    <PatientsListSection
      columns={columns}
      rows={rows}
      onRowOpen={onRowOpen}
      onSubmitSearch={onSubmitSearch}
      inputValue={inputValue}
      setInputValue={setInputValue}
      hasActiveSearch={hasActiveSearch}
      onClearSearch={onClearSearch}
      error={error}
      emptyMessage={emptyMessage}
      page={page}
      hasMore={hasMore}
      loading={loading}
      fetchPage={fetchPage}
      q={q}
      searchTruncated={searchTruncated}
    />
  );
}

// Extracted so the top-level `PatientsListView` stays lean. Also
// keeps the infinite-scroll observer + search form + table markup
// scoped to a single component boundary.
function PatientsListSection(props: {
  columns: Column<PatientListRow>[];
  rows: PatientListRow[] | null;
  onRowOpen: (patientId: string) => void;
  onSubmitSearch: (e: FormEvent) => void;
  inputValue: string;
  setInputValue: (s: string) => void;
  hasActiveSearch: boolean;
  onClearSearch: () => void;
  error: string | null;
  emptyMessage: string;
  page: number;
  hasMore: boolean;
  loading: boolean;
  fetchPage: (nextPage: number, term: string) => Promise<void>;
  q: string;
  // True when the search result set hit SEARCH_RESULT_CAP or the
  // server-side name search truncated its fetch — the strip surfaces
  // this so the user knows to refine the term.
  searchTruncated: boolean;
}): JSX.Element {
  // Infinite scroll — a sentinel div sits at the bottom of the
  // list; an IntersectionObserver fires fetchPage(page+1) when it
  // scrolls into view. rootMargin adds 200px so we start loading
  // slightly before the user reaches the actual end (feels
  // instant instead of "empty then pop"). Cleaned up on
  // hasMore/loading/page change so we don't stack observers.
  //
  // Suspended entirely while a search is active — the search branch
  // returns a single capped result set (see file header), so there's
  // no "next page" to fetch.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (props.hasActiveSearch) return;
    if (!props.hasMore || props.loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void props.fetchPage(props.page + 1, props.q);
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    props.hasActiveSearch,
    props.hasMore,
    props.loading,
    props.page,
    props.q,
    props.fetchPage,
  ]);

  const {
    columns,
    rows,
    onRowOpen,
    onSubmitSearch,
    inputValue,
    setInputValue,
    hasActiveSearch,
    onClearSearch,
    error,
    emptyMessage,
    hasMore,
    loading,
    searchTruncated,
  } = props;
  return (
    <>
      <form
        onSubmit={onSubmitSearch}
        className="mb-4 flex flex-wrap items-end gap-2"
        role="search"
        aria-label="Search patients"
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-ink">Search</span>
          <input
            type="search"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Name, address, or ZIP"
            aria-label="Search patients by name, address, or ZIP"
            maxLength={SEARCH_TERM_MAX}
            className="w-80 rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </label>
        <Button type="submit" variant="secondary">
          Search
        </Button>
        {hasActiveSearch ? (
          <Button type="button" variant="ghost" onClick={onClearSearch}>
            Clear
          </Button>
        ) : null}
      </form>

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows ?? []}
        rowKey={(r) => r.id}
        caption="Patients"
        emptyMessage={emptyMessage}
        onRowClick={(r) => onRowOpen(r.id)}
      />

      {/* Sentinel — IntersectionObserver above triggers next-page
          fetch when this scrolls into view. Text swaps between
          loading / row-count / end-of-list / capped-search so the
          user always sees something at the bottom. Observer is
          suspended during search (see effect above) — the search
          branch returns a single capped set. */}
      <div
        ref={sentinelRef}
        className="mt-3 py-2 text-center text-xs text-ink-3"
      >
        {loading
          ? hasActiveSearch
            ? 'Searching…'
            : 'Loading more…'
          : rows && rows.length > 0
            ? hasActiveSearch
              ? searchTruncated
                ? `Showing first ${rows.length} matches · refine your search for more`
                : `${rows.length} match${rows.length === 1 ? '' : 'es'}`
              : hasMore
                ? ''
                : `End of list · ${rows.length} patient${rows.length === 1 ? '' : 's'}`
            : ''}
      </div>
    </>
  );
}

